import simpleStorage from "simplestorage.js";

/**
 * Facade over the storage mechanism we utilize.
 */
class Storage {

    /**
     * This function will add an "object" of data to a "key" and store it
     * inside the SimpleStorage. Essentially, it's a key/value pair relationship.
     *
     * @param name {String} The key / name
     * @param object {Object} The object of data you ware storing.
     * @param expireInSeconds {Number} The seconds until expiration
     * @returns {*|undefined}
     */
    static setData (name, object, expireInSeconds) {
        simpleStorage.set(name, JSON.stringify(object));
        simpleStorage.setTTL(name, expireInSeconds * 1000);
    }

    /**
     * This function will get the data associated with the given name.
     *
     * @param name {String} The key / name
     * @returns {Object|null} The data or null.
     */
    static getData (name) {
        let retVal = null;
        const store = simpleStorage.get(name);

        if (null !== store && "undefined" !== typeof store) {
            retVal = JSON.parse(store);
        }

        return retVal;
    }

    /**
     * Deletes the key from storage.
     *
     * @param name {String} The key
     */
    static deleteData (name) {
        simpleStorage.deleteKey(name);
    }

    /**
     * Flushes (empties) the local storage.
     */
    static flush () {
        simpleStorage.flush();
    }

    /**
     * Set the session key
     *
     * @param key {String} The key
     * @param expireTimeInSeconds {Number} Seconds until key expires
     */
    static setSessionKey (key, expireTimeInSeconds) {
        this.setData("sessionKey", {key: key}, expireTimeInSeconds);
        this.setData("expireTime", {time: expireTimeInSeconds}, expireTimeInSeconds*2);
    }

    /**
     * Deletes the key from storage.
     */
    static getSessionKey () {
        const key = this.getData("sessionKey") || { key: null };
        return key.key;
    }

    /**
     * Set the time to live value.
     *
     * @param key {String} Key that will have its TTL set
     * @param expire {Number} Amount of seconds until it expires
     */
    static setTTL (key, expire) {
        simpleStorage.setTTL(key, expire * 1000);
    }

    /**
     * Set a temporary store (If the user was to close the tab, it will no longer be present)
     * (Falls back to a 2min ttl item)
     *
     * @param key {String} The key for the item
     * @param value {String} The value you're storing
     */
    static setTemporaryStore ({ key, value }) {
        if ("sessionStorage" in window) {
            window.sessionStorage.setItem(key, value);
        } else {
            simpleStorage.set(key, value);
            simpleStorage.setTTL(key, key, 120);

        }
    }

    /**
     * Get a temporary store item.
     *
     * @param key {String} The key lookup.
     * @returns {String} The value you stored.
     */
    static getTemporaryStore (key) {
        if ("sessionStorage" in window) {
            return window.sessionStorage.getItem(key);
        } else {
            return simpleStorage.get(key);
        }
    }

    /**
     * Delete the temporary store after using it
     * (It will clear itself after 2min or after the tab is closed & re-opened)
     *
     * @param key {String} The key lookup
     * @returns {*}
     */
    static deleteTemporaryStore (key) {
        if ("sessionStorage" in window) {
            return window.sessionStorage.removeItem(key);
        } else {
            return simpleStorage.deleteKey(key);
        }
    }

}

export default Storage;


// WEBPACK FOOTER //
// ./public/domain/controllers/storage/index.js